import { Box, Paper, SxProps, Theme } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { useAppSelector } from 'store/store';
import toast from 'shared/utils/toast';
import { t } from 'i18next';
import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { BaseService } from 'shared/services/BaseService';
import { compileDjangoQueryParams, normalizeTableItems } from '../../helpers/djangoFilterMapper';
import { cardTableLayoutStyles } from '../../styles/table';
import { RowContextMenu } from '../data-grid/components/RowContextMenu';
import { RowAction } from '../types/enhanced.types';
import { ToolbarAction, ToolbarFilter } from '../types/toolbar.types';
import EnhancedTableHead, { EnhancedHeadCell } from './components/EnhancedTableHead';
import EnhancedTableToolbar from './components/EnhancedTableToolbar';
import { EnhancedTableContextType, EnhancedTableProvider } from './context/EnhancedTableContext';

/**
 * Property contract contract managing behaviors and configurations for EnhancedTable.
 */
interface EnhancedTableProps {
  /** Core API communication framework orchestration module */
  service: BaseService<any>;

  /** Structural list defining available rendering column cell engines */
  columns: Array<{
    /** Unique identification token mapping a field from the row payload object */
    field: string;
    /** Header descriptive string presented to users in the layout column header matrix */
    label?: string;
    /** If true, formats and shifts layout typography alignments to right margins */
    numeric?: boolean;
    /** Toggle switch enabling interactive column head sorting capabilities */
    sortable?: boolean;
    /** Layout content alignment constraint targeting internal row cells */
    align?: 'left' | 'center' | 'right';
    /** Custom JSX rendering interceptor formatting rows on an individual basis */
    renderCell?: (params: { row: any; index: number }) => React.ReactNode;
  }>;
  /** Specialized query interceptor overriding standard paginated fetch routines */
  getMethod?: (limit: number, offset: number, params: any) => Promise<any>;
  /** Immutably bound base dictionary query criteria parameters injected automatically */
  initialParams?: Record<string, string>;
  /** External macro target anchor enabling remote table query re-invocations */
  refresh?: MutableRefObject<(() => void) | null>;
  /** Context bridge object extracting reactive row listings back to parent frames */
  rowsRef?: MutableRefObject<any[]>;
  /** Dynamic context filters supplied on execution by cross-component layout nodes */
  outerParams?: any;
  /** Global query key targeting full-text string properties across models */
  fullTextQuery?: string;
  /** Pre-configured complex advanced criteria block array setups loaded on initialization */
  initialFilterItems?: Array<{ field: string; operator: string; value?: string }>;

  /** Design token switching between classical spreadsheet grids or floating responsive card tiles */
  variant?: 'standard' | 'cards';
  /** Toggle switch removing text descriptive layout cell matrices from viewports */
  hideHeader?: boolean;
  /** Theme-driven structural style engine overrides customizing structural bounds */
  sx?: SxProps<Theme>;

  /** Action definitions building custom context dropdown lists attached to rows */
  rowActions?: RowAction[];
  /** Authorization switch revoking native platform row purging capabilities */
  disableDelete?: boolean;
  /** Global row interaction callback mapping events across entire layout blocks */
  onRowClick?: (row: any) => void;

  /** Initial configurations specifying properties for active toolbar filter widgets */
  toolbarFilters?: ToolbarFilter[];
  /** Action structures generating context button lists inside toolbar sections */
  toolbarActions?: ToolbarAction[];
  /** Toggle switch overriding and revoking data layout migration options */
  disableExport?: boolean;
  /** Unique relational lookup token targeting data importing wizards */
  entityId?: number;
}

/**
 * Highly abstract, presentation-agnostic query and table container.
 * Automates server-side sorting, pagination, and multi-layered data schema synchronization
 * while separating algorithmic parameter mapping from interface view architectures.
 *
 * @param props - Custom interface constraints defined by EnhancedTableProps.
 * @returns Comprehensive synchronized presentation layout engine.
 */
export const EnhancedTable = ({
  service,
  columns,
  getMethod,
  initialParams,
  refresh,
  rowsRef,
  outerParams,
  fullTextQuery,
  initialFilterItems,
  variant = 'cards',
  hideHeader = true,
  sx,
  rowActions = [],
  disableDelete = false,
  onRowClick,
  toolbarFilters = [],
  toolbarActions
}: EnhancedTableProps) => {
  const [items, setItems] = useState<any[]>([]);
  const [count, setCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [filterModel, setFilterModel] = useState<{ items: any[] }>({
    items: initialFilterItems || []
  });
  const [orderMacro, setOrderMacro] = useState<string>('');
  const [orderByField, setOrderByField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [preferenceKey, setPreferenceKey] = useState<string>('');

  const [firstRender, setFirstRender] = useState<boolean>(true);

  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const defaultWarehouse = useAppSelector((state) => state.auth.warehouse);

  // Formats and structuralizes static column nodes into EnhancedTableHead parameters
  const headCells: EnhancedHeadCell[] = columns.map((col) => ({
    id: col.field,
    label: col.label || '',
    numeric: col.numeric,
    sortable: col.sortable,
    align: col.align
  }));

  const shouldRenderActions = rowActions.length > 0 || (!!service.delete && !disableDelete);
  if (shouldRenderActions) {
    headCells.push({ id: 'row_actions_column_key', label: '', align: 'center' });
  }

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handlePageSizeChange = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(0);
  }, []);
  /**
   * Primary asynchronous fetch utility coordinating table states and calling backends.
   */
  const refreshColumnsData = useCallback(() => {
    // We remove "if (isLoading) return;" to allow new filter inputs to interrupt stale requests
    setIsLoading(true);

    const searchParams = compileDjangoQueryParams({
      filterModelItems: filterModel.items,
      filterValues,
      toolbarFilters,
      initialParams,
      fullTextQuery,
      orderMacro,
      outerParams,
      columns,
      defaultWarehouse
    });

    const offset = page * pageSize;

    const fetchPromise = getMethod
      ? getMethod(pageSize, offset, searchParams)
      : service.getAllPaginated(pageSize, offset, searchParams);

    fetchPromise
      .then((res: any) => {
        const rawResults = res?.results || res?.data || [];
        setItems(normalizeTableItems(rawResults));
        setCount(res?.count || 0);
      })
      .catch((err) => {
        console.error('Error fetching grid data:', err);
        setPage(0);
        setItems([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    page,
    pageSize,
    filterModel.items,
    filterValues,
    orderMacro,
    fullTextQuery,
    outerParams,
    initialParams,
    getMethod,
    service,
    toolbarFilters,
    columns,
    defaultWarehouse
  ]);

  // Evaluates primitive property hashes to prevent cascade render references
  const columnsDependencyKey = columns.map((col) => col.field).join(',');

  // Lifecycle Sync Phase 1: Hydrates state boundaries from cached LocalStorage parameters
  useEffect(() => {
    const pk = btoa(
      JSON.stringify({
        service: service.modulePrefix,
        getMethod: getMethod?.name || 'default',
        columns: columns.map((col) => col.field)
      })
    );
    setPreferenceKey(pk);

    const savedState = localStorage.getItem(pk);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.filter) setFilterModel({ items: parsed.filter.items || [] });
        if (parsed.sorting && parsed.sorting.length > 0) {
          const sort = parsed.sorting[0];
          setOrderByField(sort.field);
          setSortDirection(sort.sort);
          setOrderMacro(`${sort.sort === 'desc' ? '-' : ''}${sort.field}`);
        }
      } catch (e) {
        console.error('Errore nel parsing dello stato locale:', e);
      }
    }

    setFirstRender(false);
  }, [service.modulePrefix, getMethod?.name, columnsDependencyKey]);

  // Lifecycle Sync Phase 2: Intercepts criteria updates to update cache matrices and request data
  useEffect(() => {
    if (firstRender || !preferenceKey) return;

    const stateToSave = {
      filter: filterModel,
      sorting: orderByField ? [{ field: orderByField, sort: sortDirection }] : []
    };
    localStorage.setItem(preferenceKey, JSON.stringify(stateToSave));

    refreshColumnsData();
  }, [
    page,
    pageSize,
    filterModel,
    filterValues,
    orderMacro,
    fullTextQuery,
    outerParams,
    preferenceKey,
    firstRender
  ]);

  // Links and binds internal dispatch tools out onto shared parent parameters
  useEffect(() => {
    if (refresh) refresh.current = refreshColumnsData;
  }, [refresh, refreshColumnsData]);

  // Mirror-syncs working collection nodes directly across boundary objects
  useEffect(() => {
    if (rowsRef) rowsRef.current = items;
  }, [items, rowsRef]);

  // Drops pagination markers down to index 0 whenever layout constraints change
  useEffect(() => {
    setPage(0);
  }, [fullTextQuery, outerParams, filterValues]);

  /**
   * Dispatches column matrix state adjustments to rebuild ordering properties.
   */
  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderByField === property && sortDirection === 'asc';
    const nextDirection = isAsc ? 'desc' : 'asc';
    setSortDirection(nextDirection);
    setOrderByField(property);
    setOrderMacro(`${nextDirection === 'desc' ? '-' : ''}${property}`);
    setPage(0);
  };

  /**
   * Invokes delete mutations across target scopes and flushes current viewport queries.
   */
  const handleOnDelete = (id: number) => {
    service.delete(id).then(() => {
      refreshColumnsData();
      toast.success(t('actions.successfully.deleted'));
    });
  };

  const handleToolbarFiltersChange = useCallback((nextValues: Record<string, any>) => {
    setFilterValues(nextValues);
    setPage(0);
  }, []);

  const apiContextValue: EnhancedTableContextType = {
    items,
    count,
    page,
    pageSize,
    isLoading,
    filterValues,
    sortDirection,
    orderByField,
    setPage,
    setPageSize,
    refreshColumnsData,
    setFilterValues,
    handleRequestSort,
    handleOnDelete
  };

  const computedTableStyles = variant === 'cards' ? cardTableLayoutStyles : { minWidth: 750 };

  return (
    <EnhancedTableProvider value={apiContextValue}>
      <Box
        id="table-container"
        sx={{ width: '100%', backgroundColor: '#ffffff !important', ...sx }}>
        <EnhancedTableToolbar
          service={service}
          filters={toolbarFilters}
          onFiltersChange={handleToolbarFiltersChange}
          actions={toolbarActions}
        />

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ backgroundColor: '#ffffff !important' }}>
          <Table sx={computedTableStyles} aria-label="configurable enhanced table">
            <EnhancedTableHead
              headCells={headCells}
              sortDirection={sortDirection}
              orderByField={orderByField}
              onRequestSort={handleRequestSort}
              hideHeader={hideHeader}
            />

            <TableBody>
              {items.map((row, rowIndex) => (
                <TableRow key={row.id || rowIndex}>
                  {columns.map((col) => (
                    <TableCell
                      key={col.field}
                      align={col.align || (col.numeric ? 'right' : 'left')}>
                      {col.renderCell
                        ? col.renderCell({ row, index: rowIndex })
                        : `${row[col.field] ?? ''}`}
                    </TableCell>
                  ))}
                  {shouldRenderActions && (
                    <TableCell
                      align="center"
                      style={{ width: 60, paddingRight: 12 }}
                      onClick={(e) => e.stopPropagation()}>
                      <RowContextMenu
                        row={row}
                        customActions={rowActions}
                        shouldRenderDelete={!!service.delete && !disableDelete}
                        handleOnDelete={handleOnDelete}
                        t={t}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {items.length === 0 && !isLoading && (
            <Box sx={{ p: 4, textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
              Nessun record trovato
            </Box>
          )}
        </TableContainer>

        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25, 50]}
          count={count}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={(_, newPage) => handlePageChange(newPage)}
          onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
          sx={{ borderTop: 'none', mt: 1 }}
        />
      </Box>
    </EnhancedTableProvider>
  );
};

export default EnhancedTable;
